import { adminAchievementService } from './adminAchievementService';

// Achievement types that should exist for leaderboard categories
const REQUIRED_ACHIEVEMENT_TYPES = {
  EARLY_DISCOVERIES: {
    name: 'Early Discovery Master',
    description: 'Discover trending content before it becomes popular',
    category: 'DISCOVERY',
    rarity: 'rare',
    points: 50,
    icon: 'Search',
    conditionType: 'EARLY_DISCOVERIES',
    targetValue: 10,
    targetField: 'earlyDiscoveries'
  },
  REVIEW_COUNT: {
    name: 'Prolific Reviewer',
    description: 'Write helpful reviews for movies and shows',
    category: 'CONTENT',
    rarity: 'uncommon',
    points: 25,
    icon: 'RateReview',
    conditionType: 'REVIEWS_WRITTEN',
    targetValue: 50,
    targetField: 'reviewsCount'
  },
  FOLLOWER_COUNT: {
    name: 'Social Influencer',
    description: 'Build a following in the community',
    category: 'SOCIAL',
    rarity: 'rare',
    points: 75,
    icon: 'Group',
    conditionType: 'FOLLOWER_COUNT',
    targetValue: 1000,
    targetField: 'followersCount'
  },
  MOVIE_WATCHED_COUNT: {
    name: 'Movie Marathon Champion',
    description: 'Watch a large number of movies',
    category: 'CONTENT',
    rarity: 'epic',
    points: 100,
    icon: 'MovieFilter',
    conditionType: 'MOVIES_WATCHED',
    targetValue: 500,
    targetField: 'moviesWatched'
  },
  LOGIN_STREAK: {
    name: 'Dedication Master',
    description: 'Maintain long login streaks',
    category: 'LOYALTY',
    rarity: 'rare',
    points: 60,
    icon: 'LocalFireDepartment',
    conditionType: 'LOGIN_STREAK',
    targetValue: 30,
    targetField: 'loginStreak'
  }
};

export const leaderboardDataService = {
  // Check existing achievements for leaderboard compatibility
  async checkLeaderboardCompatibility() {
    try {
      console.log('Checking leaderboard compatibility...');
      
      const existingAchievements = await adminAchievementService.getAllAchievements();
      const requiredNames = ['MOVIE_CRITIC', 'SOCIAL_BUTTERFLY', 'BINGE_WATCHER', 'Review Writer', 'Liked by 5'];
      
      const results = {
        compatible: [],
        missing: [],
        total: existingAchievements.length
      };
      
      const existingNames = existingAchievements.map(a => a.name);
      
      requiredNames.forEach(name => {
        if (existingNames.includes(name)) {
          results.compatible.push(name);
        } else {
          results.missing.push(name);
        }
      });
      
      return results;
    } catch (error) {
      console.error('Error checking leaderboard compatibility:', error);
      throw error;
    }
  },
  
  // Create sample user achievements for testing
  async createSampleAchievements(userId) {
    try {
      const sampleAchievements = [
        {
          userID: userId,
          achievementType: 'MOVIE_CRITIC',
          unlocked: true,
          unlockedAt: new Date().toISOString(),
          progress: 1,
          metadata: JSON.stringify({ points: 50 })
        },
        {
          userID: userId,
          achievementType: 'Review Writer',
          unlocked: true,
          unlockedAt: new Date().toISOString(),
          progress: 1,
          metadata: JSON.stringify({ points: 5 })
        },
        {
          userID: userId,
          achievementType: 'SOCIAL_BUTTERFLY',
          unlocked: true,
          unlockedAt: new Date().toISOString(),
          progress: 1,
          metadata: JSON.stringify({ points: 25 })
        }
      ];
      
      const results = [];
      for (const achievement of sampleAchievements) {
        try {
          const result = await adminAchievementService.grantAchievement(
            userId, 
            achievement.achievementType
          );
          results.push(result);
        } catch (error) {
          console.error(`Error creating sample achievement:`, error);
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error creating sample achievements:', error);
      throw error;
    }
  },
  
  // Check leaderboard data availability
  async checkLeaderboardData() {
    try {
      const categories = [
        'EARLY_DISCOVERIES',
        'REVIEW_COUNT', 
        'FOLLOWER_COUNT',
        'MOVIE_WATCHED_COUNT',
        'LOGIN_STREAK'
      ];
      
      const results = {};
      
      for (const category of categories) {
        try {
          // This would call the actual leaderboard service
          // For now, return mock data structure
          results[category] = {
            hasData: false,
            count: 0,
            sampleUsers: []
          };
        } catch (error) {
          results[category] = {
            hasData: false,
            error: error.message
          };
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error checking leaderboard data:', error);
      throw error;
    }
  },
  
  // Fix existing achievements with wrong condition types
  async fixExistingAchievements() {
    try {
      console.log('Fixing existing achievements with wrong condition types...');
      
      const existingAchievements = await adminAchievementService.getAllAchievements();
      const results = {
        fixed: [],
        errors: []
      };
      
      // Map of achievement names to correct condition types
      const fixes = {
        'MOVIE_CRITIC': 'REVIEWS_WRITTEN',
        'THE OG': 'SPECIFIC_MOVIE_WATCHED', 
        'SOCIAL_BUTTERFLY': 'FOLLOWER_COUNT',
        'Gained Followers': 'FOLLOWER_COUNT',
        'Review Writer': 'REVIEWS_WRITTEN',
        'Liked by 5': 'LIKES_RECEIVED',
        'BINGE_WATCHER': 'MOVIES_WATCHED'
      };
      
      for (const achievement of existingAchievements) {
        const correctConditionType = fixes[achievement.name];
        if (correctConditionType && achievement.conditionType !== correctConditionType) {
          try {
            await adminAchievementService.updateAchievement(achievement.id, {
              ...achievement,
              conditionType: correctConditionType
            });
            results.fixed.push(`${achievement.name}: ${achievement.conditionType} → ${correctConditionType}`);
          } catch (error) {
            results.errors.push({
              name: achievement.name,
              error: error.message
            });
          }
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error fixing existing achievements:', error);
      throw error;
    }
  },
  
  // Seed test data for leaderboards
  async seedLeaderboardTestData() {
    try {
      console.log('Seeding leaderboard test data...');
      
      // First ensure achievement types exist
      await this.initializeLeaderboardAchievements();
      
      // Get all users to seed data for
      const users = await adminAchievementService.getAllUsers();
      const testUsers = users.slice(0, 10); // Use first 10 users
      
      const results = {
        usersProcessed: 0,
        achievementsCreated: 0,
        errors: []
      };
      
      for (const user of testUsers) {
        try {
          await this.createSampleAchievements(user.id);
          results.usersProcessed++;
          results.achievementsCreated += 3; // 3 achievements per user
        } catch (error) {
          results.errors.push({
            userId: user.id,
            error: error.message
          });
        }
      }
      
      console.log('Leaderboard test data seeding complete:', results);
      return results;
      
    } catch (error) {
      console.error('Error seeding leaderboard test data:', error);
      throw error;
    }
  }
};